import { useCallback, useEffect, useRef, useState } from 'react';
import { useCurrentUser } from '../../contexts/UserContext';

export default function AIAssistant({ lessonTitle, aiContext }) {
  const currentUser = useCurrentUser();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const logRef = useRef(null);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;

    const userMsg = { id: Date.now(), role: 'user', content: text };
    const assistantId = Date.now() + 1;
    setMessages((prev) => [...prev, userMsg, { id: assistantId, role: 'assistant', content: '' }]);
    setInput('');
    setStreaming(true);

    const context = {
      subject: 'Python Programming',
      grade: '',
      moduleTitle: lessonTitle || 'Python Lesson',
      submoduleTitle: '',
      blockHeading: aiContext?.heading || '',
      blockContent: [
        aiContext?.code ? `Current code:\n${aiContext.code}` : '',
        aiContext?.error ? `Last error:\n${aiContext.error}` : '',
      ].filter(Boolean).join('\n\n'),
      firstName: currentUser?.firstName || currentUser?.name?.split(' ')[0] || 'Student',
    };

    const history = messages.map((m) => ({ role: m.role, content: m.content }));

    try {
      const res = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, context, history }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMessages((prev) =>
          prev.map((m) => m.id === assistantId
            ? { ...m, content: err.error || 'Something went wrong.' }
            : m)
        );
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (payload === '[DONE]') break;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.text) {
              setMessages((prev) =>
                prev.map((m) => m.id === assistantId
                  ? { ...m, content: m.content + parsed.text }
                  : m)
              );
            }
          } catch { /* ignore */ }
        }
      }
    } catch {
      setMessages((prev) =>
        prev.map((m) => m.id === assistantId
          ? { ...m, content: 'Connection error. Please try again.' }
          : m)
      );
    } finally {
      setStreaming(false);
    }
  }, [input, streaming, messages, lessonTitle, aiContext, currentUser]);

  const placeholder = aiContext?.error
    ? `Error in your code — ask me what went wrong…`
    : `Ask me anything about ${aiContext?.heading || 'this lesson'}…`;

  return (
    <div className="pylab-ai">
      <div className="pylab-ai-log" ref={logRef}>
        {messages.length === 0 ? (
          <p className="pylab-ai-empty">
            {aiContext?.error
              ? 'There\'s an error in your last cell — I\'ve got the context. Ask me what\'s wrong.'
              : 'Ask me anything about this lesson — concepts, errors, or how to try something different.'}
          </p>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className={`pylab-ai-msg pylab-ai-msg--${msg.role}`}>
              <span className="pylab-ai-bubble">
                {msg.content || (msg.role === 'assistant' && streaming
                  ? <span className="pylab-ai-cursor" />
                  : null)}
              </span>
            </div>
          ))
        )}
      </div>

      <div className="pylab-ai-input-row">
        <textarea
          className="pylab-ai-textarea"
          rows={2}
          placeholder={placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
          }}
          disabled={streaming}
        />
        <button
          type="button"
          className="pylab-ai-send-btn"
          onClick={handleSend}
          disabled={streaming || !input.trim()}
        >
          {streaming ? '…' : '↑'}
        </button>
      </div>
    </div>
  );
}
